import React from 'react';
import PropTypes from 'prop-types';

import {Stack, Box, Typography, Pill} from 'views/components';
import {translate, SimpleRefreshWidget} from 'views/module';

import CaregiverStatus from './caregiver_status/CaregiverStatus';

import moment from 'moment';
import {simplifyQuestions} from 'utils/user_bio';

const CaregiverDetails = (props) => {
  const {
    caregiverData,
    caregiverBioQuestions,
    caregiverBackend,
    refresh,
    setRefresh,
  } = props;

  const [lastUpdatedAt, setLastUpdatedAt] = React.useState(new Date());

  const nullChar = '-';

  const caregiverBio = React.useMemo(() => {
    if (!caregiverBioQuestions?.questions) {
      return {};
    }

    return simplifyQuestions(caregiverBioQuestions.questions);
  }, [caregiverBioQuestions]);

  const caregiverCreatedAt = caregiverData.createdAt ?? null;
  const daysFromCreation = React.useMemo(() => {
    if (!caregiverCreatedAt) return 0;
    return moment().diff(moment(caregiverCreatedAt), 'days');
  }, [caregiverCreatedAt]);
  const caregiverLocation = [caregiverBio.city, caregiverBio.province].filter(Boolean).join(', ') || nullChar;

  const metricsSections = React.useMemo(() => {
    return [
      {
        key: 'phoneNumber',
        label: 'Phone Number',
        value: caregiverBio.phoneNumber || nullChar,
      },
      {
        key: 'email',
        label: 'Email',
        value: caregiverBio.email || nullChar,
      },
    ];
  }, [caregiverBio, nullChar]);

  return (
    <Stack sx={{gap: '10px'}}>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems={{xs: 'flex-start', md: 'center'}}
        sx={{gap: '10px', flexWrap: 'wrap'}}
      >
        <Stack
          direction='row'
          sx={{
            gap: '7px',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <CaregiverStatus
            caregiverData={caregiverData}
            caregiverBackend={caregiverBackend}
          />
          <Pill
            label={translate('client.ClientDetail.Status.Subtitles.Day', {number: daysFromCreation})}
            variant='info'
          />
          {
            caregiverLocation !== nullChar &&
            <Pill
              label={caregiverLocation}
              variant='neutral'
            />
          }
          {
            caregiverCreatedAt &&
            <Pill
              label={translate('client.ClientDetail.Status.Subtitles.Created', {
                value: moment(caregiverCreatedAt).format(translate('timeFormat.ShortMonthDayYear')),
              })}
              variant='neutral'
            />
          }
          <Pill
            label={translate('client.ClientDetail.Status.Subtitles.Updated', {
              value: moment(lastUpdatedAt).format(translate('timeFormat.ShortMonthDayTime')),
            })}
            variant='neutral'
          />
        </Stack>
        <Stack
          direction='row'
          alignItems='center'
          sx={{gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end', ml: 'auto'}}
        >
          <SimpleRefreshWidget
            numRefresh={refresh}
            setNumRefresh={setRefresh}
            setLastUpdatedAt={setLastUpdatedAt}
          />
        </Stack>
      </Stack>
      <Box
        sx={{
          boxSizing: 'border-box',
          width: '100%',
          backgroundColor: '#FFFFFF',
          border: '1px solid #E8E4DE',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gap: '1px',
            backgroundColor: '#E8E4DE',
            gridTemplateColumns: {
              xs: 'repeat(1, minmax(0, auto))',
              sm: 'repeat(2, minmax(0, auto))',
            },
          }}
        >
          {
            metricsSections.map((section) => {
              return (
                <Box
                  key={section.key}
                  sx={{
                    minWidth: 0,
                    backgroundColor: '#FFFFFF',
                    px: 3,
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Stack
                    sx={{
                      gap: '6px',
                      minWidth: 0,
                      alignItems: 'flex-start',
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: '11px',
                        lineHeight: '14px',
                        color: '#A8A49E',
                      }}
                    >
                      {section.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: '12px',
                        lineHeight: '16px',
                        color: '#000000',
                        wordBreak: 'break-word',
                      }}
                    >
                      {section.value}
                    </Typography>
                  </Stack>
                </Box>
              );
            })
          }
        </Box>
      </Box>
    </Stack>
  );
};

CaregiverDetails.propTypes = {
  caregiverData: PropTypes.object.isRequired,
  caregiverBioQuestions: PropTypes.object.isRequired,
  caregiverBackend: PropTypes.object.isRequired,
  refresh: PropTypes.number.isRequired,
  setRefresh: PropTypes.func.isRequired,
};

export default React.memo(CaregiverDetails);
